import { HttpClient, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable as __Observable, Observable, of, Subject } from 'rxjs';
import { filter as __filter, map as __map, catchError, delay, map, tap } from 'rxjs/operators';
import { ApiConfiguration as __Configuration } from '../api-configuration';
import { BaseService as __BaseService } from '../base-service';
import { CountryDTO, LookupDTO, RoomDTO, ShowCase } from '../models';
import { Floor } from '../models/showcases-dto';
import { SoldToPartyDTO } from '../models/sold-to-party-dto';
import { StoreDTO } from '../models/store-dto';
import { TimezoneDTO } from '../models/timezone-dto';
import { TrayDTO } from '../models/trays-dto';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';

@Injectable({
  providedIn: 'root'
})
export class LookupService extends __BaseService implements OnDestroy {

  // Loading stream
  private readonly loading = new Subject<boolean>();
  get loading$(): Observable<boolean> {
    return this.loading;
  }

  // Cleanup.
  ngOnDestroy() {
    this.loading.unsubscribe();
  }

  static readonly lookupPath = '/Lookup';

  static readonly usersPath = LookupService.lookupPath + '/users';
  static readonly countriesPath = LookupService.lookupPath + '/countries';
  static readonly timezonesPath = LookupService.lookupPath + '/timezones';

  static storesPath = LookupService.lookupPath + '/Stores';

  static soldToPartyPath = LookupService.lookupPath + '/SoldToParty';

  static showcasesPath(idEvent: number) {
    return LookupService.lookupPath + '/showcases/' + idEvent;
  }

  static roomsPath(idEvent: number) {
    return LookupService.lookupPath + '/' + idEvent + '/rooms';
  }
  static clientAdvisorsPath(idEvent: number) {
    return LookupService.lookupPath + '/' + idEvent + '/clientAdvisors';
  }

  static runnersPath(idEvent: number) {
    return LookupService.lookupPath + '/' + idEvent + '/runners';
  }

  static grandVendeursPath(idEvent: number) {
    return LookupService.lookupPath + '/' + idEvent + '/grandVendeurs';
  }

  static getFloorsPath(idEvent: number) {
    return LookupService.lookupPath + '/' + idEvent + '/floors';
  }

  static getTraysPath(idEvent: number) {
    return LookupService.lookupPath + '/' + idEvent + '/trays';
  }

  static getExtraTraysPath(idEvent: number) {
    return LookupService.lookupPath + '/' + idEvent + '/extratrays';
  }

  constructor(
    config: __Configuration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * GET LOOKUP ROOM
   * @param idEvent
   * @returns retrieve rooms
   */
  getRoomsResponse(idEvent: number): __Observable<__StrictHttpResponse<RoomDTO[]>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + LookupService.roomsPath(idEvent),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<RoomDTO[]>;
      })
    );
  }
  getRooms(idEvent: number): __Observable<RoomDTO[]> {
    return this.getRoomsResponse(idEvent).pipe(
      __map(_r => _r.body as RoomDTO[])
    );
  }


  getClientAdvisorsResponse(idEvent: number): __Observable<__StrictHttpResponse<LookupDTO>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + LookupService.clientAdvisorsPath(idEvent),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<LookupDTO>;
      })
    );
  }
  getClientAdvisors(idEvent: number): __Observable<LookupDTO> {
    return this.getClientAdvisorsResponse(idEvent).pipe(
      __map(_r => _r.body as LookupDTO)
    );
  }

  getRunnersResponse(idEvent: number): __Observable<__StrictHttpResponse<LookupDTO[]>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + LookupService.runnersPath(idEvent),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<LookupDTO[]>;
      })
    );
  }
  getRunners(idEvent: number): __Observable<LookupDTO[]> {
    return this.getRunnersResponse(idEvent).pipe(
      __map(_r => _r.body as LookupDTO[])
    );
  }

  getGrandVendeursResponse(idEvent: number): __Observable<__StrictHttpResponse<LookupDTO[]>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + LookupService.grandVendeursPath(idEvent),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<LookupDTO[]>;
      })
    );
  }
  getGrandVendeurs(idEvent: number): __Observable<LookupDTO[]> {
    return this.getGrandVendeursResponse(idEvent).pipe(
      __map(_r => _r.body as LookupDTO[])
    );
  }


  getUsers(name: string) {
    return this.http.get<any>(this.rootUrl + LookupService.usersPath + '?name=' + name).pipe(
      tap(() => this.loading.next(true)),
      delay(1000),
      map((res) => {
        this.loading.next(false);
        return res.items;
      }),
      // Catch any errors
      catchError((err) => {
        console.error(err);
        this.loading.next(false);
        return of(undefined);
      })
    )
  }


  /**
  * GET LOOKUP COUNTRIES
  * @param idEvent
  * @returns retrieve countries
  */
  getCountriesResponse(): __Observable<__StrictHttpResponse<CountryDTO[]>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + LookupService.countriesPath,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<CountryDTO[]>;
      })
    );
  }
  getCountries(): __Observable<CountryDTO[]> {
    return this.getCountriesResponse().pipe(
      __map(_r => _r.body as CountryDTO[])
    );
  }

  /**
   * SHOWCASES
   * @param idEvent
   * @returns Get a list of showcase
   */
  getShowcasesResponse(idEvent: number): __Observable<__StrictHttpResponse<ShowCase[]>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + LookupService.showcasesPath(idEvent),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ShowCase[]>;
      })
    );
  }
  getShowcases(idEvent: number): __Observable<ShowCase[]> {
    return this.getShowcasesResponse(idEvent).pipe(
      __map(_r => _r.body as ShowCase[])
    );
  }

  /**
* GET LOOKUP COUNTRIES
* @param idEvent
* @returns retrieve countries
*/
  getStoresResponse(): __Observable<__StrictHttpResponse<StoreDTO[]>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + LookupService.storesPath,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<StoreDTO[]>;
      })
    );
  }
  getStores(): __Observable<StoreDTO[]> {
    return this.getStoresResponse().pipe(
      __map(_r => _r.body as StoreDTO[])
    );
  }

  /**
   * GET SOLD PARTIES
   * @param idEvent - Id dell'evento
   * @returns Get SoldParties
   */
  getSoldToPartyResponse(idEvent?: number): __Observable<__StrictHttpResponse<SoldToPartyDTO[]>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + LookupService.soldToPartyPath + "/" + idEvent,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<SoldToPartyDTO[]>;
      })
    );
  }
  getSoldToParty(idEvent?: number): __Observable<SoldToPartyDTO[]> {
    return this.getSoldToPartyResponse(idEvent).pipe(
      __map(_r => _r.body as SoldToPartyDTO[])
    );
  }

  /**
 * GET LOOKUP COUNTRIES
 * @param idEvent
 * @returns retrieve countries
 */
  getTimezonesResponse(): __Observable<__StrictHttpResponse<TimezoneDTO[]>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + LookupService.timezonesPath,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TimezoneDTO[]>;
      })
    );
  }
  getTimezones(): __Observable<TimezoneDTO[]> {
    return this.getTimezonesResponse().pipe(
      __map(_r => _r.body as TimezoneDTO[])
    );
  }


  /**
   **GET - /api/bo/Lookup/{idEvent}/floors
   * @param idEvent
   * @returns Ritorna i piani della location di dove si terrà l'evento
   */
  getFloorsResponse(idEvent: number): __Observable<__StrictHttpResponse<Floor[]>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + LookupService.getFloorsPath(idEvent),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Floor[]>;
      })
    );
  }
  getFloors(idEvent: number): __Observable<Floor[]> {
    return this.getFloorsResponse(idEvent).pipe(
      __map(_r => _r.body as Floor[])
    );
  }

  /**
   **GET - /api/bo/Lookup/{idEvent}/trays
   * @param idEvent
   * @returns Ritorna i vassoi
   */
  getTraysResponse(idEvent: number, date: any, areExtraTrays?: boolean): __Observable<__StrictHttpResponse<TrayDTO[]>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    if (date) {
      __params = __params.append("ReferenceDate", date.format('YYYY-MM-DD'));
    }

    let req = new HttpRequest<any>(
      'GET',
      areExtraTrays ?
        this.rootUrl + LookupService.getExtraTraysPath(idEvent)
        :
        this.rootUrl + LookupService.getTraysPath(idEvent),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TrayDTO[]>;
      })
    );
  }

  getTrays(idEvent: number, date: any, areExtraTrays?: boolean): __Observable<TrayDTO[]> {
    return this.getTraysResponse(idEvent, date, areExtraTrays).pipe(
      __map(_r => _r.body as TrayDTO[])
    );
  }

}
